import { CalendarDays } from 'lucide-react';
import React from 'react';

function parseDate(iso: string): Date {
  return new Date(iso);
}

function fmtMedium(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtShort(d: Date): string {
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export type DeliveryWindowBadgeVariant = 'compact' | 'split';

/**
 * Delivery batch / order shipping window (estimated window from backend).
 */
export function DeliveryWindowBadge({
  windowStart,
  windowEnd,
  fromLabel,
  untilLabel,
  variant = 'split',
  className = '',
}: {
  windowStart: string;
  windowEnd: string;
  fromLabel: string;
  untilLabel: string;
  variant?: DeliveryWindowBadgeVariant;
  className?: string;
}) {
  const a = parseDate(windowStart);
  const b = parseDate(windowEnd);
  if (variant === 'compact') {
    return (
      <span
        className={[
          'inline-flex max-w-full items-center gap-1 rounded-full border border-teal-300/80 bg-gradient-to-r from-teal-50 to-cyan-50 px-2 py-0.5 text-[10px] font-bold text-teal-900 shadow-sm',
          className,
        ].join(' ')}
      >
        <CalendarDays className="h-3 w-3 shrink-0 opacity-90" aria-hidden />
        <span className="truncate tabular-nums">
          {fmtShort(a)} → {fmtShort(b)}
        </span>
      </span>
    );
  }
  return (
    <div className={['flex flex-wrap items-center gap-1.5', className].join(' ')}>
      <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-[11px] font-semibold text-sky-900 shadow-sm">
        <span className="text-[10px] font-bold uppercase tracking-wide text-sky-600/90">{fromLabel}</span>
        <span className="tabular-nums">{fmtMedium(a)}</span>
      </span>
      <span className="inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold text-violet-900 shadow-sm">
        <span className="text-[10px] font-bold uppercase tracking-wide text-violet-600/90">{untilLabel}</span>
        <span className="tabular-nums">{fmtMedium(b)}</span>
      </span>
    </div>
  );
}

/** Estimated lead time in days (checkout / order summary). */
export function EstimatedDaysBadge({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-900 shadow-sm',
        className,
      ].join(' ')}
    >
      <CalendarDays className="h-3.5 w-3.5 shrink-0 text-emerald-700" aria-hidden />
      {children}
    </span>
  );
}
